<template>
  <div class="template-repeatable-list">
    <div v-if="items.length === 0" class="empty-hint">
      <em>No {{ itemNoun }} yet.</em>
    </div>

    <draggable
      :model-value="items"
      item-key="_key"
      handle=".drag-handle"
      ghost-class="drag-ghost"
      :animation="150"
      @update:model-value="onReorder"
    >
      <template #item="{ element, index }">
        <div class="repeatable-item">
          <div class="repeatable-controls">
            <button
              type="button"
              class="btn btn-sm btn-link drag-handle"
              title="Drag to reorder"
              :aria-label="`Reorder ${itemNoun} ${index + 1}`"
            >
              <i class="fa fa-grip-vertical"></i>
            </button>
            <span class="item-number">{{ index + 1 }}</span>
            <button
              type="button"
              class="btn btn-sm btn-link text-danger ms-auto"
              :aria-label="`Remove ${itemNoun} ${index + 1}`"
              @click="remove(index)"
            >
              <i class="fa fa-trash"></i> Remove
            </button>
          </div>
          <div class="repeatable-body">
            <slot name="item" :item="element" :index="index"></slot>
          </div>
        </div>
      </template>
    </draggable>

    <div class="mt-2">
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        :disabled="atMax"
        @click="add"
      >
        <i class="fa fa-plus"></i> Add {{ itemNoun }}
      </button>
      <span v-if="atMax" class="text-muted small ms-2">
        Maximum {{ maxItems }} reached.
      </span>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

let keyCounter = 0;

export default {
  name: 'TemplateRepeatableList',

  components: { draggable },

  props: {
    modelValue: { type: Array, required: true },
    newItem: { type: Function, required: true },
    maxItems: { type: Number, default: 0 },
    itemNoun: { type: String, default: 'item' },
  },

  emits: ['update:modelValue'],

  computed: {
    // Wrap items with a stable _key so drag reordering + add/remove don't
    // confuse Vue's list reconciliation. Keys are internal only; stripped on emit.
    items() {
      return this.modelValue.map(item => {
        if (item && typeof item === 'object') {
          if (!item._key) item._key = `rpt-${++keyCounter}`;
          return item;
        }
        // Primitive values get wrapped for key tracking.
        return { _key: `rpt-${++keyCounter}`, _value: item };
      });
    },
    atMax() {
      return this.maxItems > 0 && this.modelValue.length >= this.maxItems;
    },
  },

  methods: {
    emitItems(items) {
      // Strip internal _key markers before emitting.
      const clean = items.map(i => {
        if (i && typeof i === 'object' && '_value' in i && Object.keys(i).length === 2) {
          return i._value;
        }
        const { _key, ...rest } = i;
        return rest;
      });
      this.$emit('update:modelValue', clean);
    },
    add() {
      if (this.atMax) return;
      const next = [...this.modelValue, this.newItem()];
      this.$emit('update:modelValue', next);
    },
    remove(index) {
      const next = this.modelValue.slice();
      next.splice(index, 1);
      this.$emit('update:modelValue', next);
    },
    onReorder(newItems) {
      this.emitItems(newItems);
    },
  },
};
</script>

<style scoped>
.template-repeatable-list .empty-hint {
  padding: 0.75rem 1rem;
  background: #fafafa;
  border: 1px dashed #ddd;
  border-radius: 4px;
  color: #888;
  text-align: center;
}
.template-repeatable-list .repeatable-item {
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #fff;
  margin-bottom: 0.75rem;
  overflow: hidden;
}
.template-repeatable-list .repeatable-controls {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background: #f7f7f7;
  border-bottom: 1px solid #eee;
  gap: 0.5rem;
}
.template-repeatable-list .drag-handle {
  cursor: grab;
  color: #999;
  padding: 0.25rem 0.5rem;
}
.template-repeatable-list .drag-handle:active {
  cursor: grabbing;
}
.template-repeatable-list .item-number {
  font-weight: 600;
  color: #666;
  font-size: 0.85rem;
}
.template-repeatable-list .repeatable-body {
  padding: 0.75rem 1rem;
}
.template-repeatable-list .drag-ghost {
  opacity: 0.4;
  background: #fef3f8;
}
</style>
